/**
 * Redeem API hooks for user code redemption
 * Provides React Query hooks for redeeming codes and viewing history
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { authenticatedApi } from '../lib/authenticated-api';
import { auth } from '../lib/firebase';
import toast from 'react-hot-toast';
import {
  EMAIL_NOT_VERIFIED_MESSAGES,
  isEmailNotVerifiedError,
} from '../lib/emailNotVerified';
import type {
  RedeemCodeResponse,
  RedemptionHistoryResponse,
  RedeemPreviewResponse,
} from '../lib/types/redeemGroup';

// Query keys for cache management
const redeemQueryKeys = {
  all: ['redeem'] as const,
  history: (limit?: number) => ['redeem', 'history', limit] as const,
  preview: (code: string) => ['redeem', 'preview', code] as const,
};

// 30 words at ~200 wpm is ~9 s, and the reader then has to act on it (#2151); hover pauses it, pointer only.
const EMAIL_NOT_VERIFIED_TOAST_MS = 10_000;

/**
 * Hook to redeem a promotional code
 * Invalidates history cache on success
 * Shows toast notifications for success/error
 */
export function useRedeemCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await authenticatedApi.redeemCode(code);
      if (!response.success || !response.data) {
        throw new Error(String(response.error) || 'Failed to redeem code');
      }
      return response.data;
    },
    onSuccess: async (data: RedeemCodeResponse) => {
      // Invalidate history queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: redeemQueryKeys.all });

      // Force a token refresh so the new entitlements are actually usable in
      // this session. Access checks read the custom claims off the decoded ID
      // token, which Firebase caches for up to an hour — so without this a
      // redemption that fully succeeded on the backend stays invisible to the
      // user until the token happens to rotate. Same reason and same call as
      // the post-checkout path in SubscriptionSuccessPage.
      //
      // Skipped when the backend says the claims are not there yet: at THIS
      // instant re-minting can only reproduce the same stale claims, because
      // Firebase mints from what Auth holds — which is also why signing out and
      // back in does not help. The success panel offers the same refresh as an
      // explicit action for later, when a retry or an operator resync may have
      // written them; that is a different moment, not a contradiction.
      if (data.entitlementsActive && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (error) {
          console.error('Token refresh failed after redemption:', error);
        }
      }

      if (!data.entitlementsActive) {
        toast.success(
          'Code redeemed. Your access is being applied and will appear shortly.'
        );
        return;
      }

      // Show success message with granted items
      const items = [];
      if (data.entitlements.length > 0) {
        items.push(`${data.entitlements.length} entitlement(s)`);
      }
      if (data.badges.length > 0) {
        items.push(`${data.badges.length} badge(s)`);
      }
      const itemsText = items.length > 0 ? ` - ${items.join(', ')}` : '';
      toast.success(`Code redeemed successfully${itemsText}`);
    },
    onError: (error: unknown) => {
      // The backend refuses redemption to an unverified address (#2037), by
      // category, not by status: 403 means other things too.
      if (isEmailNotVerifiedError(error)) {
        toast.error(EMAIL_NOT_VERIFIED_MESSAGES.redeem, {
          duration: EMAIL_NOT_VERIFIED_TOAST_MS,
        });
        return;
      }
      let message = 'Failed to redeem code';
      if (error && typeof error === 'object') {
        if ('message' in error && typeof error.message === 'string') {
          // Extract HTTP status code from error message format "HTTP {status}: {text}"
          const errorMessage = error.message;
          const httpStatusMatch = errorMessage.match(/^HTTP (\d+):/);

          if (httpStatusMatch) {
            const statusCode = parseInt(httpStatusMatch[1], 10);

            // Handle specific HTTP error codes
            if (statusCode === 404) {
              message = 'Invalid code - code not found';
            } else if (statusCode === 409) {
              message = 'This code has already been redeemed';
            } else if (statusCode === 400) {
              message = 'This code is from a disabled group';
            } else if (statusCode === 401) {
              message = 'You must be logged in to redeem codes';
            } else {
              message = errorMessage;
            }
          } else {
            message = errorMessage;
          }
        }
      }
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch user's redemption history with pagination
 * Uses infinite query with "Load More" functionality
 */
export function useRedemptionHistory(limit = 20) {
  return useInfiniteQuery<RedemptionHistoryResponse>({
    queryKey: redeemQueryKeys.history(limit),
    queryFn: async ({ pageParam }) => {
      const response = await authenticatedApi.getRedemptionHistory(
        limit,
        pageParam as string | undefined
      );
      if (!response.success || !response.data) {
        throw new Error(
          String(response.error) || 'Failed to fetch redemption history'
        );
      }
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      // Return the code of the last item to use as cursor for next page
      if (lastPage.hasMore && lastPage.redemptions.length > 0) {
        return lastPage.redemptions[lastPage.redemptions.length - 1].code;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to preview what a code would grant without redeeming it
 * Returns entitlementIds, badgeIds, and groupName
 * Only enabled when code is provided
 */
export function useRedeemPreview(code: string | null) {
  return useQuery<RedeemPreviewResponse>({
    queryKey: redeemQueryKeys.preview(code || ''),
    queryFn: async () => {
      if (!code) {
        throw new Error('Code is required');
      }
      const response = await authenticatedApi.getRedeemPreview(code);
      if (!response.success || !response.data) {
        throw new Error(String(response.error) || 'Failed to fetch preview');
      }
      return response.data;
    },
    enabled: !!code,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });
}
